/**
 * PatientRefundsList — Refunds sub-tab of Patient Billing
 * (Tier-3 patient_billing).
 *
 * Flutter ref: `patient_billing/.../data/datamodels/patient_refund_list_model.dart`
 * + `patient_billing_data_source_impl.dart:89-101`.
 *
 * The patient-scoped refund endpoint returns refunds across BOTH advance
 * receipts and invoice payments for the patient, with paging. Different
 * from the invoice-scoped `useRefundsForInvoice` in `use-payments.ts:53`.
 *
 * **Envelope shape (verified 2026-05-20 dev curl):** the backend ships
 * the generic `PagedResults<T>` envelope (`pageNumber / pageSize /
 * totalNumberOfPages / totalNumberOfRecords / results`), same as the
 * sibling `/AdvancePaymentReceipts`. NOT Flutter's documented
 * `PatientRefundListModel` (`count / currentPage / totalPage /
 * refundList`) — that file is stale because no Flutter UI calls this
 * endpoint, so the model never gets deserialized.
 */

import { useState } from 'react';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientRefundsList } from '@/patients/services/use-patient-refunds-list';
import {
  BillingListFilterBar,
  EMPTY_BILLING_FILTER,
  type BillingFilterValue,
} from './billing-list-filter-bar';
import { BillingPager } from './billing-pager';

import { formatMoney } from '@/shared/lib/money';
import { V2_BODY_EMPHASIS, V2_CARD_SURFACE, v2Look } from '@/shared/ui/design-v2';
// Verbatim Flutter `app_constants.dart` payment_method_* values.
const METHOD_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'Card', label: 'Card' },
  { value: 'Bank', label: 'Bank' },
];

export interface PatientRefundsListProps {
  patientId: string | null | undefined;
}

export function PatientRefundsList({ patientId }: PatientRefundsListProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<BillingFilterValue>(
    EMPTY_BILLING_FILTER,
  );
  const onFilters = (next: BillingFilterValue) => {
    setPage(1);
    setFilters(next);
  };

  const { data, isFetching } = usePatientRefundsList({
    patientId,
    pageNumber: page,
    pageSize,
    query: filters.search || undefined,
    startDate: filters.startDate,
    endDate: filters.endDate,
    // `refundMethod: []` (not null) when no filter — backend 500s on null.
    refundMethod: filters.facets.method ?? [],
  });

  const rows = data?.results ?? [];
  const totalPages = data?.totalNumberOfPages ?? 1;
  const totalRecords = data?.totalNumberOfRecords ?? 0;
  const hasActiveFilters =
    !!filters.search ||
    !!filters.startDate ||
    (filters.facets.method?.length ?? 0) > 0;

  const filterBar = (
    <BillingListFilterBar
      searchPlaceholder="Search by refund #"
      facetGroups={[{ key: 'method', title: 'Method', options: METHOD_OPTIONS }]}
      value={filters}
      onChange={onFilters}
    />
  );

  if (isFetching && rows.length === 0) {
    return (
      <div className="space-y-3">
        {filterBar}
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        {filterBar}
        <div className="rounded-lg border border-dashed border-border-default p-8 text-center">
          <p className="text-sm text-text-muted">
            {hasActiveFilters
              ? 'No refunds match these filters.'
              : 'No refunds on file for this patient.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filterBar}
      <div className={v2Look('rounded-lg border border-border-default overflow-hidden', V2_CARD_SURFACE)}>
        <table className="w-full text-sm">
          <thead className="bg-grey-100">
            <tr className="text-left text-text-muted">
              <Th>Refund #</Th>
              <Th>Linked receipt</Th>
              <Th>Refund date</Th>
              <Th>Method</Th>
              <Th align="right">Amount</Th>
              <Th>Linked invoice</Th>
              <Th>Refunded by</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.id ?? row.refundReceiptNumber ?? `row-${i}`}
                className="border-t border-border-default hover:bg-grey-100/50"
              >
                <Td>
                  {/* PROD-2743 · switch ON: a record number is a value, not a link: body-emphasis in
                      text-primary, not brand ink. OFF = today. */}
                  <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                    {row.refundReceiptNumber ?? '—'}
                  </span>
                </Td>
                <Td>
                  {row.linkedReceiptNumber ? (
                    <span className="text-text-secondary">
                      {row.linkedReceiptNumber}
                    </span>
                  ) : (
                    <span className="text-text-hint">—</span>
                  )}
                </Td>
                <Td>{formatDate(row.refundDate)}</Td>
                <Td>{row.refundMethod ?? '—'}</Td>
                <Td align="right">AED {formatMoney(row.amount ?? 0)}</Td>
                <Td>
                  {row.invoiceNumber ? (
                    <span className="text-text-secondary">
                      {row.invoiceNumber}
                    </span>
                  ) : (
                    <span className="text-text-hint">—</span>
                  )}
                </Td>
                <Td>
                  {row.refundedBy?.firstName || row.refundedBy?.lastName ? (
                    <span className="text-text-secondary">
                      {[row.refundedBy?.firstName, row.refundedBy?.lastName]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  ) : (
                    <span className="text-text-hint">—</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BillingPager
        page={page}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        isFetching={isFetching}
        noun="refunds"
        onPage={setPage}
        onPageSize={(n) => {
          setPage(1);
          setPageSize(n);
        }}
      />
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-3 py-2 text-xs font-bold uppercase tracking-wide ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <td
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </td>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}
